import { supabase } from "@/integrations/supabase/client";
import { Property } from "@/types/property";
import { handleAsyncError } from "@/utils/errorUtils";
import mapSupabaseProperty from "@/utils/mappers/propertyMapper";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";

// Utility function to sort properties by operation_started_at with NULLS LAST
const sortPropertiesByOperationDate = (properties: Property[]): Property[] => {
  return [...properties].sort((a, b) => {
    // Properties without date go to the end
    const aHasDate = a.operationStartedAt && a.operationStartedAt !== '';
    const bHasDate = b.operationStartedAt && b.operationStartedAt !== '';
    
    if (!aHasDate && !bHasDate) return 0;
    if (!aHasDate) return 1;  // a goes to the end
    if (!bHasDate) return -1; // b goes to the end
    
    // Both have dates: sort ascending
    return a.operationStartedAt.localeCompare(b.operationStartedAt);
  });
};

export const useProperties = () => {
  const location = useLocation();
  const isAdmin = location?.pathname?.startsWith("/admin");

  const { profile } = useAuth();
  const role = profile?.role;
  const userId = profile?.user_id;

  const skipRoleFilter = !isAdmin;

  return useQuery({
    queryKey: ["properties", role, userId, skipRoleFilter],
    queryFn: async (): Promise<Property[]> => {
      const result = await handleAsyncError(async () => {
        let query;

        if (skipRoleFilter || role === "super_admin") {
          query = supabase
            .from("properties")
            .select(
              `
              *,
              hosts (*),
              neighborhoods (*),
              property_highlights (*),
              reviews (*)
            `,
            )
            .order("operation_started_at", { ascending: true, nullsFirst: false });
        } else if (userId) {
          query = supabase
            .from("user_property_assignments")
            .select(
              `
              *,
              properties (
                *,
                hosts (*),
                neighborhoods (*),
                property_highlights (*),
                reviews (*)
              )
            `,
            )
            .eq("user_id", userId);
        } else {
          return [];
        }

        const { data, error } = await query;

        if (error) {
          console.error("Supabase error:", error);
          throw error;
        }

        if (skipRoleFilter || role === "super_admin") {
          return sortPropertiesByOperationDate((data ?? []).map(mapSupabaseProperty));
        }

        return sortPropertiesByOperationDate(
          data?.map((assignment) =>
            mapSupabaseProperty(assignment.properties),
          ) ?? []
        );
      }, "Properties fetch");

      return result ?? [];
    },
    enabled: skipRoleFilter || !!role,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useProperty = (propertyId: string) => {
  return useQuery({
    queryKey: ["property", propertyId],
    queryFn: async (): Promise<Property | null> => {
      const result = await handleAsyncError(async () => {
        const { data: property, error } = await supabase
          .from("properties")
          .select(
            `
            *,
            hosts (*),
            neighborhoods (*),
            property_highlights (*),
            reviews (*)
          `,
          )
          .eq("id", propertyId)
          .single();

        if (error) {
          throw error;
        }

        return property;
      }, `Property ${propertyId} fetch`);

      if (!result) {
        return null;
      }

      const mappedProperty = mapSupabaseProperty(result);

      return mappedProperty;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};